import { memo } from 'react';

interface LightningAnimationProps {
  /** Flash animation duration in seconds (default: 4) */
  duration?: number;
  /** Delay before animation starts in seconds (default: 0) */
  delay?: number;
  /** Pause between each flash iteration in seconds (default: 1) */
  iterationDelay?: number;
  /** Horizontal movement distance (default: -4) */
  moveX?: number;
  /** Vertical movement distance (default: 8) */
  moveY?: number;
}

/**
 * Lightning animation with synchronized motion and opacity.
 *
 * Animation cycle:
 * - 0%: hidden, at top position
 * - 0-10%: fade in while moving
 * - 10-90%: visible while moving
 * - 90-100%: fade out while moving
 * - 100%: hidden, at bottom position
 * - Then pause before next cycle
 */
function LightningAnimation({
  duration = 4,
  delay = 0,
  iterationDelay = 1,
  moveX = -4,
  moveY = 8,
}: LightningAnimationProps) {
  // Total cycle = flash duration + pause between flashes
  const totalCycle = duration + iterationDelay;
  // Calculate what fraction of the cycle is the active flash
  const flashRatio = duration / totalCycle;

  // Opacity keyTimes scaled to flash portion:
  // 0% -> 10% fade in, 10% -> 90% visible, 90% -> 100% fade out, then hidden during pause
  const opacityKeyTimes = [0, 0.1 * flashRatio, 0.9 * flashRatio, flashRatio, 1]
    .map((v) => v.toFixed(4))
    .join('; ');

  // Motion keyPoints: move along path during flash, stay at end during pause
  // 0 = start of path, 1 = end of path
  const motionKeyPoints = [0, 1, 1].map((v) => v.toFixed(4)).join('; ');
  const motionKeyTimes = [0, flashRatio, 1].map((v) => v.toFixed(4)).join('; ');

  return (
    <>
      {/* Opacity: fade in 0-10%, visible 10-90%, fade out 90-100%, hidden during pause */}
      <animate
        attributeName="opacity"
        values="0; 1; 1; 0; 0"
        keyTimes={opacityKeyTimes}
        dur={`${totalCycle}s`}
        begin={`${delay}s`}
        repeatCount="indefinite"
        calcMode="spline"
        keySplines="0.4 0 0.6 1; 0.5 0 0.5 1; 0.4 0 0.6 1; 0.5 0 0.5 1"
      />
      {/* Diagonal downward motion - moves during flash, holds at end during pause */}
      <animateMotion
        path={`M0,0 L${moveX},${moveY}`}
        keyPoints={motionKeyPoints}
        keyTimes={motionKeyTimes}
        dur={`${totalCycle}s`}
        begin={`${delay}s`}
        repeatCount="indefinite"
        calcMode="spline"
        keySplines="0.4 0 0.6 1; 0.5 0 0.5 1"
      />
    </>
  );
}

export default memo(LightningAnimation);
